import { flattenedRoutes } from "@/routes";
import { Key } from "react";
import { RouteObject } from "react-router-dom";

/**
 * ex: /company/:id => /company/1
 * @param {string} pathname - pathname is define in AppRoutes
 * @param {number} params - Object to replace params in dynamic route
 * @returns {string} - DynamicRoute.
 *
 */
export const getDynamicRoute = (pathname: string, params: Record<string, Key | undefined>, searchParams?: string): string => {
  const route = Object.entries(params).reduce((cur, [key, value]) => (value ? cur.replace(`:${key}`, value.toString()) : cur), pathname);
  return searchParams ? `${route}?${searchParams}` : route;
};

/**
 * ex: /company/:id => /company/1
 * @param {string} url - origin url
 * @param {number} params - Object to create search param
 * @returns {string} - url with search param.
 *
 */

export const createUrlWithParams = <D = object>(url: string, data: D): string => {
  if (!data) {
    return url;
  }

  const dataAry = Object.entries<number | string | boolean | string[] | number[]>(data).filter(([_, value]) => {
    return !(value === null || value === undefined);
  });
  if (!dataAry.length) {
    return url;
  }

  const params = dataAry.map(([key, value]) => {
    // For arrays use the same key and create a key=value string for each value
    if (Array.isArray(value)) {
      return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join("&");
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  });

  return [url, params.join("&")].join("?");
};

/**
 * @param {string} url - url to verify
 * @returns {boolean} - true isUrl
 */
export const isUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * @returns {string} - return tenant domain
 */

export const getTenantDomain = (): string => {
  const url = window.location.hostname;
  return url.replace("admin.", "");
};

/**
 * @param {string} url - url to verify
 * @returns {boolean} - true isInternalTenant
 */
export const isInternalTenantLink = (url: string): boolean => {
  try {
    const linkUrl = new URL(url);
    return linkUrl.hostname.includes(getTenantDomain());
  } catch (error) {
    return false;
  }
};

/**
 * Extracts query parameters from a given URL and returns them as an object.
 *
 * @template T - Type of the query parameters. Should be a record where keys are strings and values are strings or arrays of strings or undefined.
 * @param {string} url - The URL from which to extract query parameters.
 * @returns {T | null} - An object containing the query parameters, where keys are parameter names and values are their corresponding values. Returns `null` if an error occurs.
 */
export const getQuery = <T extends Record<string, string | string[] | undefined>>(url: string): T | null => {
  try {
    const { searchParams } = new URL(url); // Create a URL object and extract searchParams
    const initData: Record<string, string | string[] | undefined> = {}; // Initialize an empty object to store query parameters

    return Array.from(searchParams).reduce((cur, [key, value]) => {
      const curValue = cur[key];
      if (curValue) {
        // If the key already exists, merge the new value with the existing value(s)
        cur[key] = [curValue, value].flat();
        return cur;
      }
      cur[key] = value; // Otherwise, set the key with the new value
      return cur;
    }, initData) as T; // Cast the result to the specified type T
  } catch {
    return null; // Return null if an error occurs during URL parsing or query extraction
  }
};

/**
 * Extracts query parameters from the current URL.
 *
 * @template T - Type of the query parameters. Should be a record where keys are strings and values are strings or arrays of strings or undefined.
 * @returns {T | null} - An object containing the query parameters, or `null` if no parameters are found.
 */
export const getQueryFromURL = <T extends Record<string, string | string[] | undefined>>(): T | null => {
  return getQuery(window.location.toString());
};

/**
 * Extracts dynamic route parameters from a given origin and pathname.
 *
 * @param {string} origin - The origin part of the URL, typically containing dynamic segments (e.g., "/users/:userId/posts/:postId").
 * @param {string} pathname - The actual path of the URL (e.g., "/users/123/posts/456").
 * @returns {Record<string, string>} - An object mapping dynamic segment names to their values (e.g., { userId: "123", postId: "456" }).
 */
export const getParams = (origin: string, pathname: string): Record<string, string> => {
  const originSplit = origin.split("/"); // Split the origin into segments based on "/"
  const pathnameSplit = pathname.split("/"); // Split the pathname into segments based on "/"
  const params: Record<string, string> = {}; // Object to store the extracted parameters

  originSplit.forEach((item, index) => {
    if (item.startsWith(":")) {
      const key = item.replace(":", ""); // Remove ":" to get the parameter key
      const value = pathnameSplit[index]; // Get the corresponding value from the pathname
      if (key && value) {
        params[key] = value; // Assign the value to the parameter key
      }
    }
  });

  return params; // Return the object containing the extracted parameters
};

export const buildCategoryRoute = (route: string, categoryName?: string): string => {
  if (!categoryName) {
    return route.replace("/:categoryName?", "");
  }
  return route.replace(":categoryName?", encodeURIComponent(categoryName));
};

export const getMatchRoutes = () => {
  const pathname = window.location.pathname;
  const splitLocationPath = pathname?.split("/") ?? [];
  const matches: RouteObject[] = [];
  const matchWithOptional = (routeSegments: string[], locSegments: string[]): boolean => {
    // helpers for readability
    const isDynamic = (seg: string) => seg.startsWith(":");
    const isOptional = (seg: string) => isDynamic(seg) && seg.endsWith("?");

    let routeIdx = 0; // index into routeSegments
    let locIdx = 0; // index into locSegments

    // Walk both segment arrays and try to match them
    while (routeIdx < routeSegments.length && locIdx < locSegments.length) {
      const routeSeg = routeSegments[routeIdx];
      const locSeg = locSegments[locIdx];

      // Exact literal match
      if (routeSeg === locSeg) {
        routeIdx++;
        locIdx++;
        continue;
      }

      // Dynamic segment (param)
      if (isDynamic(routeSeg)) {
        if (isOptional(routeSeg)) {
          // If next route segment exists and matches current location segment,
          // treat this dynamic segment as absent (skip it).
          const nextRouteSeg = routeSegments[routeIdx + 1];
          if (nextRouteSeg && nextRouteSeg === locSeg) {
            routeIdx++; // skip optional param without consuming a location segment
            continue;
          }

          // Otherwise consume the current location segment as the optional param value
          routeIdx++;
          locIdx++;
          continue;
        }

        // Required dynamic segment consumes one location segment
        routeIdx++;
        locIdx++;
        continue;
      }

      // Literal mismatch and not a dynamic param
      return false;
    }

    // Any remaining route segments must all be optional dynamic segments
    for (let k = routeIdx; k < routeSegments.length; k++) {
      const seg = routeSegments[k];
      if (!isOptional(seg)) return false;
    }

    // If there are remaining location segments that weren't consumed, it's not a match
    if (locIdx < locSegments.length) return false;

    return true;
  };

  flattenedRoutes.forEach((route) => {
    const splitRoutePath = route.path?.split("/") ?? [];
    if (matchWithOptional(splitRoutePath, splitLocationPath)) {
      matches.push(route);
    }
  });
  return matches;
};
